import findMessengerConversations from "@faire/web-api--source/endpoints/www/api/messenger/list-conversations/post";
import fetchMessengerConversationByBrand from "@faire/web-api--source/endpoints/www/api/v3/messenger/brand-conversation/brandToken/get";
import fetchMessengerConversation from "@faire/web-api--source/endpoints/www/api/v3/messenger/conversation/conversationToken/get";
import fetchMessengerConversationByRetailer from "@faire/web-api--source/endpoints/www/api/v3/messenger/retailer-conversation/retailerToken/get";
import { trackMessengerMessageFromBrandToRetailer } from "@faire/web-api--source/events/messenger/actionUnknown/messageFromBrandToRetailer";
import { trackMessengerMessageFromRetailerToBrand } from "@faire/web-api--source/events/messenger/actionUnknown/messageFromRetailerToBrand";
import { IGetConversationResponseV3 } from "@faire/web-api--source/faire/messenger/IGetConversationResponseV3";
import { IGetMessengerConversationsRequest } from "@faire/web-api--source/indigofair/data/IGetMessengerConversationsRequest";
import { IGetMessengerConversationsResponse } from "@faire/web-api--source/indigofair/data/IGetMessengerConversationsResponse";
import { IMessengerConversation } from "@faire/web-api--source/indigofair/data/IMessengerConversation";
import { IUser } from "@faire/web-api--source/indigofair/data/IUser";
import { useStrictLocalization } from "@faire/web/common/localization";
import { logError } from "@faire/web/common/logging";
import compact from "lodash/compact";
import isEqual from "lodash/isEqual";
import { useCallback, useMemo, useRef } from "react";

import { getBrandTokenInSession } from "../utils";

import {
  ConversationModel,
  getConversationToken as getConversationModelToken,
  getConversationIdentifier as getConversationModelIdentifier,
  isConversationUnRead as isConversationModelUnRead,
  isConversationStarredByBrand as isConversationModelStarredByBrand,
  createSyntheticStatusMessage,
  createConversationModel,
  updateConversation as updateConversationModel,
  useFirstFetchOrUpdateMessages,
} from "./ConversationModel";
import {
  setIsFirstFetch,
  setNextRequest,
  setLastMainStreamConversationRequestTokens,
} from "./MainStreamFetchState";
import { useMessengerContext } from "./MessengerContext";
import { useMessengerStoreConversationIdentification } from "./useMessengerStoreConversationIdentification";
import { useMessengerStoreConversationStorage } from "./useMessengerStoreConversationStorage";
import { useMessengerStoreMainStreamFetchState } from "./useMessengerStoreMainStreamFetchState";
import { useMessengerStoreStoreUpdatedConversation } from "./useMessengerStoreStoreUpdatedConversation";

/**
 * Hooks used internally for fetching conversations. Note these are referenced by
 * hooks that are brought in on the visitor pages so *all* hooks here must at least
 * support being *defined* in the absence of a MessengerStore.
 */
export const useMessengerStoreConversationFetching = (): {
  createOrUpdateConversation: (
    conversation: IMessengerConversation,
    simple_brand?: IGetMessengerConversationsResponse.ISimpleBrand,
    simple_retailer?: IGetMessengerConversationsResponse.ISimpleRetailer,
    userArg?: IUser
  ) => void;
  nextConversations: () => Promise<void>;
  searchConversations: (
    params: Pick<IGetMessengerConversationsRequest, "context_token" | "query">
  ) => Promise<void>;
  fetchConversations: (
    data?: Partial<IGetMessengerConversationsRequest>,
    isMainStreamFetch?: boolean,
    isSearch?: boolean,
    userArg?: IUser
  ) => Promise<void>;
  firstFetchOrUpdateConversations: (userArg?: IUser) => Promise<void>;
  firstFetchOrUpdateMessagesInCurrentConversation: () => Promise<void>;
  setCurrentConversation: (param?: {
    otherPartyUserToken?: string;
    brandToken?: string;
    retailerToken?: string;
    conversationToken?: string;
  }) => Promise<void>;
  hasConversationsNextRequest: boolean;
} => {
  const { strictLocalize } = useStrictLocalization();
  const { storeUpdatedConversation } =
    useMessengerStoreStoreUpdatedConversation();
  const { getConversationByIdentifier } =
    useMessengerStoreConversationIdentification();
  const {
    conversationByConversationToken,
    conversationByBrandToken,
    conversationByRetailerToken,
    conversationByUserToken,
  } = useMessengerStoreConversationStorage();
  const { mainStreamFetchState } = useMessengerStoreMainStreamFetchState();
  const { updateConversationInFetchState, existsInFetchState } =
    useMessengerStoreStoreUpdatedConversation();

  const setAllMainStreamFetchState = useMessengerContext(
    ({ setAllMainStreamFetchState }) => setAllMainStreamFetchState
  );
  const setUnreadOnlyMainStreamFetchState = useMessengerContext(
    ({ setUnreadOnlyMainStreamFetchState }) => setUnreadOnlyMainStreamFetchState
  );
  const setStarredByBrandOnlyMainStreamFetchState = useMessengerContext(
    ({ setStarredByBrandOnlyMainStreamFetchState }) =>
      setStarredByBrandOnlyMainStreamFetchState
  );
  const user = useMessengerContext(({ user }) => user);
  const latestMessageUpdatedAt = useMessengerContext(
    ({ latestMessageUpdatedAt }) => latestMessageUpdatedAt
  );
  const setLatestMessageUpdatedAt = useMessengerContext(
    ({ setLatestMessageUpdatedAt }) => setLatestMessageUpdatedAt
  );
  const setSearchResults = useMessengerContext(
    ({ setSearchResults }) => setSearchResults
  );
  const incrementIsLoadingCount = useMessengerContext(
    ({ incrementIsLoadingCount }) => incrementIsLoadingCount
  );
  const decrementIsLoadingCount = useMessengerContext(
    ({ decrementIsLoadingCount }) => decrementIsLoadingCount
  );
  const brandContext = useMessengerContext(({ brandContext }) => brandContext);
  const currentConversationIdentifier = useMessengerContext(
    ({ currentConversationIdentifier }) => currentConversationIdentifier
  );
  const setCurrentConversationIdentifier = useMessengerContext(
    ({ setCurrentConversationIdentifier }) => setCurrentConversationIdentifier
  );
  const query = useMessengerContext(({ query }) => query);
  const setTotalUnreadConversations = useMessengerContext(
    ({ setTotalUnreadConversations }) => setTotalUnreadConversations
  );
  const setTotalStarredByBrandConversations = useMessengerContext(
    ({ setTotalStarredByBrandConversations }) =>
      setTotalStarredByBrandConversations
  );
  const retailerContext = useMessengerContext(
    ({ retailerContext }) => retailerContext
  );
  const showOnlyUnreadConversations = useMessengerContext(
    ({ showOnlyUnreadConversations }) => showOnlyUnreadConversations
  );
  const showOnlyStarredByBrandConversations = useMessengerContext(
    ({ showOnlyStarredByBrandConversations }) =>
      showOnlyStarredByBrandConversations
  );

  const userFromStore = user;
  const createOrUpdateConversation = useCallback(
    // eslint-disable-next-line complexity
    (
      conversation: IMessengerConversation,
      simple_brand?: IGetMessengerConversationsResponse.ISimpleBrand,
      simple_retailer?: IGetMessengerConversationsResponse.ISimpleRetailer,
      userArg?: IUser
    ) => {
      const user = userArg ?? userFromStore;
      if (
        !user ||
        !setAllMainStreamFetchState ||
        !setUnreadOnlyMainStreamFetchState ||
        !setStarredByBrandOnlyMainStreamFetchState ||
        !setLatestMessageUpdatedAt
      ) {
        return;
      }

      const otherUser = conversation.other_party;
      const otherIsBrandUser =
        otherUser && otherUser.type === IUser.Type.BRAND_USER;
      const otherIsRetailerUser =
        otherUser && otherUser.type === IUser.Type.USER;
      const otherPartyUserToken =
        conversation.other_party && conversation.other_party.token;

      // Multi-brand allows users to be linked to multiple brands and switch between them.
      // As a result, a WebSocket channel may have been initiated and connected by a different brand.
      // Therefore, when receiving a message from the WebSocket, we need to verify if the message is intended for the current brand.
      // More details in https://www.notion.so/faire/Test-Spec-Multi-Brand-1272efb5c25a80bd853dcc6bf66f9791?pvs=4#1512efb5c25a80299108eb4fd5453f5b
      const currentBrandToken = getBrandTokenInSession();
      const isMessageForAnotherBrand =
        otherIsRetailerUser && conversation.brand_token !== currentBrandToken;

      if (!otherUser || !otherPartyUserToken || isMessageForAnotherBrand) {
        return;
      }

      // Note: We MUST use a functional update here to ensure that multiple
      // updates in the same render cycle get accumulated, as the
      // `updateConversationModel` function takes the union of messages and
      // messages may be dropped if the most recent conversation model simply
      // overwrites the previous one.
      let updatedConversation: ConversationModel | undefined = undefined;
      setAllMainStreamFetchState((mainStreamFetchState) => {
        let existingConversation: ConversationModel | undefined;
        if (conversation.token) {
          existingConversation =
            mainStreamFetchState.conversationByConversationToken.get(
              conversation.token
            );
        } else if (otherIsBrandUser && conversation.brand_token) {
          existingConversation =
            mainStreamFetchState.conversationByBrandToken.get(
              conversation.brand_token
            );
        } else if (otherIsRetailerUser && otherUser.retailer_token) {
          existingConversation =
            mainStreamFetchState.conversationByRetailerToken.get(
              otherUser.retailer_token
            );
        }

        // Create or update the conversation
        if (existingConversation) {
          updatedConversation = updateConversationModel(
            existingConversation,
            conversation,
            simple_brand,
            simple_retailer
          );
        } else {
          updatedConversation = createConversationModel(
            conversation,
            strictLocalize,
            user,
            simple_brand,
            simple_retailer
          );
        }

        updatedConversation = createSyntheticStatusMessage(
          updatedConversation,
          updatedConversation.data?.other_party_status
        );

        // Create a new state object with the updated conversation
        return updateConversationInFetchState(
          mainStreamFetchState,
          updatedConversation
        );
      });

      // However, we don't have to re-apply the changes for the other
      // fetch states, as the previous conversation is guaranteed to be the
      // same, so we just have to stick in the new conversation.
      if (updatedConversation) {
        // Using a type annotation here is safer than using `as` or `!`.
        const updatedConversationTypecast: ConversationModel =
          updatedConversation;
        setUnreadOnlyMainStreamFetchState((unreadOnlyMainStreamFetchState) => {
          if (
            isConversationModelUnRead(updatedConversationTypecast) ||
            existsInFetchState(
              unreadOnlyMainStreamFetchState,
              updatedConversationTypecast
            )
          ) {
            return updateConversationInFetchState(
              unreadOnlyMainStreamFetchState,
              updatedConversationTypecast
            );
          }
          return unreadOnlyMainStreamFetchState;
        });

        setStarredByBrandOnlyMainStreamFetchState(
          (starredByBrandOnlyMainStreamFetchState) => {
            if (
              isConversationModelStarredByBrand(updatedConversationTypecast) ||
              existsInFetchState(
                starredByBrandOnlyMainStreamFetchState,
                updatedConversationTypecast
              )
            ) {
              return updateConversationInFetchState(
                starredByBrandOnlyMainStreamFetchState,
                updatedConversationTypecast
              );
            }
            return starredByBrandOnlyMainStreamFetchState;
          }
        );
      }

      // Track conversation messages as needed
      if (
        conversation.latest_message?.author_token === otherUser.token &&
        otherIsBrandUser &&
        latestMessageUpdatedAt !== conversation.latest_message?.updated_at &&
        conversation.latest_message?.text !== "review request"
      ) {
        trackMessengerMessageFromBrandToRetailer(
          conversation?.retailer_token ?? "",
          conversation.brand_token ?? "",
          conversation.latest_message?.updated_at?.toString() ?? "",
          conversation.latest_message?.text ?? ""
        );
        setLatestMessageUpdatedAt(conversation.latest_message?.updated_at);
      }

      if (
        conversation.latest_message?.author_token === otherUser.token &&
        otherIsRetailerUser &&
        latestMessageUpdatedAt !== conversation.latest_message?.updated_at &&
        conversation.latest_message?.text !== "review request"
      ) {
        trackMessengerMessageFromRetailerToBrand(
          "",
          conversation.brand_token ?? "",
          conversation?.retailer_token ?? "",
          conversation.latest_message?.updated_at?.toString() ?? "",
          conversation.latest_message?.text ?? "",
          ""
        );
      }
    },
    [
      userFromStore,
      setAllMainStreamFetchState,
      setUnreadOnlyMainStreamFetchState,
      setStarredByBrandOnlyMainStreamFetchState,
      setLatestMessageUpdatedAt,
      latestMessageUpdatedAt,
      updateConversationInFetchState,
      strictLocalize,
      existsInFetchState,
    ]
  );

  const createOrUpdateConversations = useCallback(
    (
      conversations: IMessengerConversation[],
      simple_brands: Partial<
        Record<string, IGetMessengerConversationsResponse.ISimpleBrand>
      >,
      simple_retailers: Partial<
        Record<string, IGetMessengerConversationsResponse.ISimpleRetailer>
      >,
      userArg?: IUser
    ) => {
      const user = userArg ?? userFromStore;
      if (!user) {
        return;
      }

      conversations.forEach((conversation) => {
        if (!conversation.other_party) {
          throw new Error("An unknown error occurred");
        }
        const brand = simple_brands[conversation.brand_token!];
        const retailer = simple_retailers[conversation.retailer_token!];

        createOrUpdateConversation(conversation, brand, retailer, user);
      });
    },
    [createOrUpdateConversation, userFromStore]
  );

  const fetchConversationByToken = useCallback(
    async (
      conversationToken?: string,
      retailerToken?: string,
      brandToken?: string
    ): Promise<void> => {
      if (
        !user ||
        !incrementIsLoadingCount ||
        !setSearchResults ||
        !decrementIsLoadingCount
      ) {
        return;
      }

      try {
        incrementIsLoadingCount();

        let response: IGetConversationResponseV3;

        if (conversationToken) {
          response = await fetchMessengerConversation(conversationToken);
        } else if (user?.type === IUser.Type.BRAND_USER && retailerToken) {
          response = await fetchMessengerConversationByRetailer(retailerToken);
        } else if (user?.type === IUser.Type.USER && brandToken) {
          response = await fetchMessengerConversationByBrand(brandToken);
        } else {
          logError(
            new Error(
              "This function should recieve a conversation_token, or appropriate [brand | retailer]_token relative to the user"
            )
          );
          return;
        }

        const { conversation, simple_brand, simple_retailer } = response;

        if (!conversation || (!simple_retailer && !simple_brand)) {
          logError(
            new Error(
              "This request should return a conversation and information about the retailer or the brand"
            )
          );
          return;
        }

        if (!query && !brandContext) {
          setSearchResults(undefined);
        }

        if (conversation) {
          createOrUpdateConversation(
            conversation,
            simple_brand,
            simple_retailer
          );
        }
      } catch (e) {
        logError(e);
      } finally {
        decrementIsLoadingCount();
      }
    },
    [
      user,
      incrementIsLoadingCount,
      query,
      brandContext,
      setSearchResults,
      createOrUpdateConversation,
      decrementIsLoadingCount,
    ]
  );

  const fetchConversations = useCallback(
    // eslint-disable-next-line complexity
    async (
      data: Partial<IGetMessengerConversationsRequest> = {},
      isMainStreamFetch?: boolean,
      isSearch?: boolean,
      userArg?: IUser
    ): Promise<void> => {
      const user = userArg ?? userFromStore;
      if (
        !mainStreamFetchState ||
        !createOrUpdateConversations ||
        !setAllMainStreamFetchState ||
        !setUnreadOnlyMainStreamFetchState ||
        !setStarredByBrandOnlyMainStreamFetchState ||
        !setSearchResults ||
        !setTotalUnreadConversations ||
        !setTotalStarredByBrandConversations
      ) {
        return;
      }

      try {
        const {
          conversations,
          total_unread_conversations,
          total_starred_by_brand_conversations,
          simple_brands = {},
          simple_retailers = {},
          next_request,
        } = await findMessengerConversations(
          IGetMessengerConversationsRequest.build({
            ...data,
            context_token:
              data.context_token ??
              brandContext?.token ??
              retailerContext?.token,
            only_unread:
              data.only_unread || showOnlyUnreadConversations || undefined,
            filter: showOnlyStarredByBrandConversations
              ? IGetMessengerConversationsRequest.Filter.STARRED_BY_BRAND
              : undefined,
          })
        );

        if (!conversations) {
          return;
        }

        if (isSearch) {
          setSearchResults(
            compact(
              conversations.map(
                (conversation) =>
                  conversation.other_party && conversation.other_party.token
              )
            ).reduce((set, token) => set.add(token), new Set<string>())
          );
        } else if (!query && !brandContext) {
          setSearchResults(undefined);
        }

        if (isMainStreamFetch) {
          const conversationTokens = conversations.map(
            (conversation) => conversation.token
          );
          if (
            isEqual(
              mainStreamFetchState.lastMainStreamConversationRequestTokens,
              conversationTokens
            )
          ) {
            // If we received no new conversation tokens, it implies there's no
            // next request.
            switch (true) {
              case showOnlyUnreadConversations:
                setUnreadOnlyMainStreamFetchState((state) =>
                  setNextRequest(state, undefined)
                );
                break;
              case showOnlyStarredByBrandConversations:
                setStarredByBrandOnlyMainStreamFetchState((state) =>
                  setNextRequest(state, undefined)
                );
                break;
              default:
                setAllMainStreamFetchState((state) =>
                  setNextRequest(state, undefined)
                );
            }
            return;
          }

          // Update mainStreamFetchState with tokens and nextRequest
          switch (true) {
            case showOnlyUnreadConversations:
              setUnreadOnlyMainStreamFetchState((state) => {
                const updatedState = setLastMainStreamConversationRequestTokens(
                  state,
                  conversationTokens
                );
                return setNextRequest(updatedState, next_request);
              });
              break;
            case showOnlyStarredByBrandConversations:
              setStarredByBrandOnlyMainStreamFetchState((state) => {
                const updatedState = setLastMainStreamConversationRequestTokens(
                  state,
                  conversationTokens
                );
                return setNextRequest(updatedState, next_request);
              });
              break;
            default:
              setAllMainStreamFetchState((state) => {
                const updatedState = setLastMainStreamConversationRequestTokens(
                  state,
                  conversationTokens
                );
                return setNextRequest(updatedState, next_request);
              });
          }

          setTotalUnreadConversations(total_unread_conversations);
          setTotalStarredByBrandConversations(
            total_starred_by_brand_conversations
          );
        }

        if (brandContext && !simple_brands[brandContext.token!]) {
          simple_brands[brandContext.token!] = brandContext;
        }

        createOrUpdateConversations(
          conversations,
          simple_brands,
          simple_retailers,
          user
        );
      } catch (e) {
        logError(e);
      }
    },
    [
      userFromStore,
      mainStreamFetchState,
      createOrUpdateConversations,
      setAllMainStreamFetchState,
      setUnreadOnlyMainStreamFetchState,
      setStarredByBrandOnlyMainStreamFetchState,
      setSearchResults,
      setTotalUnreadConversations,
      setTotalStarredByBrandConversations,
      brandContext,
      retailerContext?.token,
      showOnlyUnreadConversations,
      showOnlyStarredByBrandConversations,
      query,
    ]
  );

  /*
   * Async
   *
   * Keeping persistent data that doesn't have an ordering with unlimited scroll is very difficult.
   * - needs to be persistent because
   *   - we don't know if the user is looking at it (scroll)
   * - order of conversation is constantly changing based on which ones have the newest messages
   */

  /**
   * When messenger mounts for the first time this populate conversations and nextRequest for scroll
   * Future calls makes sure all conversations are up to date
   *
   * TODO: should this be debounced? first call does something but subsequent calls do nothing until 200ms later?
   */
  const firstFetchOrUpdateConversations = useCallback(
    async (userArg?: IUser) => {
      const user = userArg ?? userFromStore;
      if (
        !user ||
        !mainStreamFetchState ||
        !setUnreadOnlyMainStreamFetchState ||
        !setStarredByBrandOnlyMainStreamFetchState ||
        !setAllMainStreamFetchState
      ) {
        return;
      }

      if (mainStreamFetchState.isFirstFetch) {
        // Update the isFirstFetch flag
        switch (true) {
          case showOnlyUnreadConversations:
            setUnreadOnlyMainStreamFetchState((state) =>
              setIsFirstFetch(state, false)
            );
            break;
          case showOnlyStarredByBrandConversations:
            setStarredByBrandOnlyMainStreamFetchState((state) =>
              setIsFirstFetch(state, false)
            );
            break;
          default:
            setAllMainStreamFetchState((state) =>
              setIsFirstFetch(state, false)
            );
        }
        return fetchConversations(undefined, true, false, user);
      } else {
        // TODO: keep fetching conversations until we find an unchanged conversation
        //   currently this fails if more than 1 page of conversations has changed
        //   requires refactoring fetchConversations and update conversation to return data about what happens
        return fetchConversations(undefined, false, false, user);
      }
    },
    [
      userFromStore,
      mainStreamFetchState,
      setUnreadOnlyMainStreamFetchState,
      setStarredByBrandOnlyMainStreamFetchState,
      setAllMainStreamFetchState,
      fetchConversations,
      showOnlyUnreadConversations,
      showOnlyStarredByBrandConversations,
    ]
  );

  const nextConversations = useCallback(async () => {
    if (!mainStreamFetchState) {
      return;
    }
    if (mainStreamFetchState.nextRequest) {
      return fetchConversations(mainStreamFetchState.nextRequest, true);
    }
  }, [fetchConversations, mainStreamFetchState]);

  const searchConversations = useCallback(
    async (
      params: Pick<IGetMessengerConversationsRequest, "context_token" | "query">
    ) => {
      await fetchConversations(params, false, true);
    },
    [fetchConversations]
  );

  const firstFetchOrUpdateMessages = useFirstFetchOrUpdateMessages();

  const firstFetchOrUpdateMessagesInCurrentConversation =
    useCallback(async () => {
      if (!user) {
        return;
      }
      if (!currentConversationIdentifier) {
        return;
      }
      const currentConversation = getConversationByIdentifier(
        currentConversationIdentifier
      );
      if (!currentConversation) {
        return;
      }
      storeUpdatedConversation(
        await firstFetchOrUpdateMessages(currentConversation)
      );
    }, [
      firstFetchOrUpdateMessages,
      currentConversationIdentifier,
      getConversationByIdentifier,
      storeUpdatedConversation,
      user,
    ]);

  const attemptToFindAndSetConversation = useCallback(
    (
      conversationToken?: string,
      brandToken?: string,
      retailerToken?: string,
      otherPartyUserToken?: string
    ): boolean => {
      if (
        !user ||
        !conversationByConversationToken ||
        !setCurrentConversationIdentifier
      ) {
        return false;
      }

      let conversation: ConversationModel | undefined;

      if (
        conversationToken &&
        conversationByConversationToken?.get(conversationToken)
      ) {
        conversation = conversationByConversationToken?.get(conversationToken);
      } else {
        // Since we're only getting brand or retailer tokens, we have to use them to do the initial look-up
        if (brandToken && conversationByBrandToken?.get(brandToken)) {
          conversation = conversationByBrandToken?.get(brandToken);
        } else if (
          retailerToken &&
          conversationByRetailerToken?.get(retailerToken)
        ) {
          conversation = conversationByRetailerToken?.get(retailerToken);
        } else if (
          otherPartyUserToken &&
          conversationByUserToken?.get(otherPartyUserToken)
        ) {
          conversation = conversationByUserToken?.get(otherPartyUserToken);
        }

        // If we found a real conversation, let's pull it from the conversation_token indexed list for consistency
        if (
          conversation &&
          getConversationModelToken(conversation) &&
          conversationByConversationToken?.get(
            getConversationModelToken(conversation)
          )
        ) {
          conversation = conversationByConversationToken?.get(
            getConversationModelToken(conversation)
          );
        }
      }

      if (conversation) {
        setCurrentConversationIdentifier(
          getConversationModelIdentifier(conversation)
        );
        return true;
      }
      return false;
    },
    [
      conversationByBrandToken,
      conversationByConversationToken,
      conversationByRetailerToken,
      conversationByUserToken,
      setCurrentConversationIdentifier,
      user,
    ]
  );

  // Use the most up-to-date version of the mutation after `await`-ing inserting
  // the conversation into the store, so we're able to find it.
  const attemptToFindAndSetConversationAfterAwait = useRef(
    attemptToFindAndSetConversation
  );
  attemptToFindAndSetConversationAfterAwait.current =
    attemptToFindAndSetConversation;

  const setCurrentConversation = useCallback(
    async (
      param: {
        otherPartyUserToken?: string;
        brandToken?: string;
        retailerToken?: string;
        conversationToken?: string;
      } = {}
    ): Promise<void> => {
      if (!user || !setCurrentConversationIdentifier) {
        return;
      }

      const {
        otherPartyUserToken,
        brandToken,
        retailerToken,
        conversationToken,
      } = param;

      if (
        !conversationToken &&
        !otherPartyUserToken &&
        !brandToken &&
        !retailerToken
      ) {
        setCurrentConversationIdentifier(undefined);
        return;
      }

      if (
        attemptToFindAndSetConversation(
          conversationToken,
          brandToken,
          retailerToken,
          otherPartyUserToken
        )
      ) {
        return;
      }
      await fetchConversationByToken(
        conversationToken,
        retailerToken,
        brandToken
      );
      if (
        !attemptToFindAndSetConversationAfterAwait.current(
          conversationToken,
          brandToken,
          retailerToken,
          otherPartyUserToken
        )
      ) {
        throw new Error(
          "A conversation should have been returned by the server."
        );
      }
    },
    [
      user,
      attemptToFindAndSetConversation,
      fetchConversationByToken,
      setCurrentConversationIdentifier,
    ]
  );

  const hasConversationsNextRequest = !!mainStreamFetchState?.nextRequest;

  return useMemo(
    () => ({
      createOrUpdateConversation,
      nextConversations,
      searchConversations,
      fetchConversations,
      firstFetchOrUpdateConversations,
      firstFetchOrUpdateMessagesInCurrentConversation,
      setCurrentConversation,
      hasConversationsNextRequest,
    }),
    [
      createOrUpdateConversation,
      nextConversations,
      searchConversations,
      fetchConversations,
      firstFetchOrUpdateConversations,
      firstFetchOrUpdateMessagesInCurrentConversation,
      setCurrentConversation,
      hasConversationsNextRequest,
    ]
  );
};
